import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail, downloadExport } from "../lib/api";
import { PageHeader, EmptyState, Confirm } from "../components/Shared";
import { TableSkeleton } from "../components/Skeletons";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../context/AuthContext";
import { ROLES, fmtFCFA, fmtDate } from "../lib/format";
import { toast } from "sonner";
import { Plus, Trash2, Printer, ArrowLeft, Pencil, Receipt as ReceiptIcon, CheckCircle2, Ban, Undo2, Search, FileSpreadsheet } from "lucide-react";

const blankItem = () => ({ item: "", unit: "", quantity: "", unit_cost: "" });
const blankForm = () => ({ received_from: "", notes: "", items: [blankItem()] });
const STATUS_STYLES = { draft: "bg-slate-100 text-slate-600", pending: "bg-amber-50 text-amber-700", approved: "bg-emerald-50 text-emerald-700", cancelled: "bg-red-50 text-red-700" };
const num = (v) => (parseFloat(v) || 0);

export default function Receipts() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [mode, setMode] = useState("list");
  const [form, setForm] = useState(blankForm());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightId, setHighlightId] = useState(null);

  const { data: rows = [], isLoading } = useQuery({ queryKey: ["receipts"], queryFn: () => api.get("/receipts").then((r) => r.data) });

  const focusId = searchParams.get("focus");
  useEffect(() => {
    if (!focusId || isLoading) return;
    setHighlightId(focusId);
    const el = document.getElementById(`receipt-row-${focusId}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    const t = setTimeout(() => {
      setHighlightId(null);
      const p = new URLSearchParams(searchParams); p.delete("focus"); setSearchParams(p, { replace: true });
    }, 4000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, isLoading]);

  const counts = rows.reduce((a, r) => { a.all += 1; a[r.status] = (a[r.status] || 0) + 1; return a; }, { all: 0, draft: 0, pending: 0, approved: 0, cancelled: 0 });
  const q = search.trim().toLowerCase();
  const visibleRows = rows.filter((r) => {
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (q && !(`${r.number || ""}`.toLowerCase().includes(q) || `${r.received_from || ""}`.toLowerCase().includes(q))) return false;
    return true;
  });
  const TABS = [{ k: "all", label: "All" }, { k: "draft", label: "Draft" }, { k: "pending", label: "Pending" }, { k: "approved", label: "Approved" }, { k: "cancelled", label: "Cancelled" }];

  const startNew = () => { setForm(blankForm()); setEditingId(null); setMode("form"); };
  const startEdit = (r) => {
    setForm({
      received_from: r.received_from || "", notes: r.notes || "",
      items: (r.items || []).map((it) => ({ item: it.item || "", unit: it.unit || "", quantity: it.quantity ?? "", unit_cost: it.unit_cost ?? "" })),
    });
    if (!(r.items || []).length) setForm((f) => ({ ...f, items: [blankItem()] }));
    setEditingId(r.id); setMode("form");
  };

  const setItem = (idx, key, val) => setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, [key]: val } : it)) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, blankItem()] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.length > 1 ? f.items.filter((_, i) => i !== idx) : f.items }));

  const grand = form.items.reduce((s, it) => s + num(it.quantity) * num(it.unit_cost), 0);

  const save = async () => {
    if (!form.received_from.trim()) { toast.error("The 'Received from' name is required."); return; }
    if (!form.items.some((it) => it.item.trim())) { toast.error("Add at least one item."); return; }
    setSaving(true);
    const payload = {
      received_from: form.received_from, notes: form.notes,
      items: form.items.filter((it) => it.item.trim() || num(it.quantity) || num(it.unit_cost))
        .map((it) => ({ item: it.item, unit: it.unit, quantity: num(it.quantity), unit_cost: num(it.unit_cost) })),
    };
    try {
      if (editingId) await api.put(`/receipts/${editingId}`, payload);
      else await api.post("/receipts", payload);
      qc.invalidateQueries({ queryKey: ["receipts"] });
      toast.success(editingId ? "Receipt updated." : "Draft saved — review it, then send for approval when ready.");
      setMode("list");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save receipt."); }
    finally { setSaving(false); }
  };

  const act = async (id, path, okMsg) => {
    try { await api.post(`/receipts/${id}/${path}`); qc.invalidateQueries({ queryKey: ["receipts"] }); toast.success(okMsg); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed."); }
  };
  const remove = async (id) => {
    try { await api.delete(`/receipts/${id}`); qc.invalidateQueries({ queryKey: ["receipts"] }); toast.success("Receipt deleted."); }
    catch { toast.error("Could not delete."); }
  };
  const preview = (r) => downloadExport(`/export/receipt/${r.id}.pdf`, `receipt_${r.number}.pdf`);

  if (mode === "form") {
    return (
      <div className="space-y-6" data-testid="receipt-form">
        <div className="flex items-center justify-between">
          <PageHeader title={editingId ? "Edit Receipt" : "New Receipt"} subtitle="Fill the details and items. The PDF carries the official company letterhead and dual signatures." />
          <Button variant="outline" onClick={() => setMode("list")}><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Received from *</Label><Input data-testid="receipt-received-from" value={form.received_from} onChange={(e) => setForm({ ...form, received_from: e.target.value })} placeholder="Name of person / company" /></div>
            <div className="space-y-1.5"><Label>Date</Label><Input value={new Date().toLocaleDateString()} disabled data-testid="receipt-date" /><p className="text-xs text-slate-400">Auto-set to the print date</p></div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="px-3 py-2 text-left w-12">S/N</th>
                  <th className="px-3 py-2 text-left">Item</th>
                  <th className="px-3 py-2 text-left w-24">Unit</th>
                  <th className="px-3 py-2 text-right w-24">Quantity</th>
                  <th className="px-3 py-2 text-right w-32">Unit cost</th>
                  <th className="px-3 py-2 text-right w-36">Total</th>
                  <th className="px-3 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((it, idx) => (
                  <tr key={idx} className="border-t border-slate-100" data-testid={`receipt-item-${idx}`}>
                    <td className="px-3 py-2 text-slate-500">{idx + 1}</td>
                    <td className="px-2 py-1.5"><Input data-testid={`receipt-item-name-${idx}`} value={it.item} onChange={(e) => setItem(idx, "item", e.target.value)} placeholder="Item" /></td>
                    <td className="px-2 py-1.5"><Input value={it.unit} onChange={(e) => setItem(idx, "unit", e.target.value)} placeholder="pcs / m / hr" /></td>
                    <td className="px-2 py-1.5"><Input type="number" min="0" step="any" className="text-right" data-testid={`receipt-item-qty-${idx}`} value={it.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)} /></td>
                    <td className="px-2 py-1.5"><Input type="number" min="0" step="any" className="text-right" data-testid={`receipt-item-cost-${idx}`} value={it.unit_cost} onChange={(e) => setItem(idx, "unit_cost", e.target.value)} /></td>
                    <td className="px-3 py-2 text-right font-mono-num whitespace-nowrap" data-testid={`receipt-item-total-${idx}`}>{fmtFCFA(num(it.quantity) * num(it.unit_cost))}</td>
                    <td className="px-2 py-2 text-center"><button onClick={() => removeItem(idx)} data-testid={`receipt-item-remove-${idx}`} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="p-3 border-t border-slate-100">
            <Button variant="outline" onClick={addItem} data-testid="receipt-add-item"><Plus className="h-4 w-4 mr-2" /> Add item</Button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-1.5"><Label>Notes</Label><Textarea data-testid="receipt-notes" rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional note…" /></div>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-center justify-between text-base font-semibold">
            <span>Grand Total</span><span className="font-mono-num text-[#1E3A8A]" data-testid="receipt-grand-total">{fmtFCFA(grand)}</span>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setMode("list")}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="receipt-save" className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">{saving ? "Saving…" : (editingId ? "Update Receipt" : "Save Receipt")}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="receipts-page">
      <div className="flex items-center justify-between">
        <PageHeader title="Receipts" subtitle="Issue official receipts. Receipts need the other officer's approval before they can be printed." />
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => downloadExport("/export/receipts?format=xlsx", "stems_receipts.xlsx")} data-testid="receipt-export-btn"><FileSpreadsheet className="h-4 w-4 mr-2" /> Export Excel</Button>
          <Button onClick={startNew} data-testid="new-receipt-btn" className="bg-[#1E3A8A] hover:bg-[#1D4ED8]"><Plus className="h-4 w-4 mr-2" /> New Receipt</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="receipt-status-tabs">
        {TABS.map((t) => (
          <button
            key={t.k}
            onClick={() => setStatusFilter(t.k)}
            data-testid={`receipt-tab-${t.k}`}
            className={`text-sm font-medium rounded-full px-4 py-1.5 border transition-colors ${statusFilter === t.k ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
          >
            {t.label} <span className={`ml-1 text-xs ${statusFilter === t.k ? "text-blue-100" : "text-slate-400"}`}>{counts[t.k] || 0}</span>
          </button>
        ))}
        <div className="relative ml-auto w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="receipt-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by number or name…" className="pl-9" />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {isLoading ? <TableSkeleton cols={8} /> : rows.length === 0 ? (
          <EmptyState text="No receipts yet." action={<Button onClick={startNew} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]"><Plus className="h-4 w-4 mr-2" /> New Receipt</Button>} />
        ) : visibleRows.length === 0 ? (
          <EmptyState text={q ? "No receipts match your search." : `No ${statusFilter} receipts.`} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Number</TableHead><TableHead>Received from</TableHead><TableHead>Created by</TableHead>
                <TableHead>Date</TableHead><TableHead className="text-right">Grand Total</TableHead><TableHead>Status</TableHead><TableHead>Approved by</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleRows.map((r) => {
                const isApprover = user.role === r.approver_role;
                const isCreator = user.id === r.created_by;
                const verb = r.approver_role === ROLES.FM ? "Verify" : "Approve";
                const sendTo = r.approver_role === ROLES.FM ? "FM" : "CEO";
                return (
                  <TableRow key={r.id} id={`receipt-row-${r.id}`} data-testid="receipt-row" className={highlightId === r.id ? "bg-amber-50 ring-2 ring-inset ring-amber-300 transition-colors" : "transition-colors"}>
                    <TableCell className="font-semibold">{r.number}</TableCell>
                    <TableCell>{r.received_from || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-500">{r.created_by_name} <span className="text-xs">({r.created_by_role})</span></TableCell>
                    <TableCell className="text-sm text-slate-500">{fmtDate(r.created_at)}</TableCell>
                    <TableCell className="text-right font-mono-num">{fmtFCFA(r.grand_total)}</TableCell>
                    <TableCell><span className={`text-xs font-semibold rounded-full px-2 py-1 ${STATUS_STYLES[r.status] || STATUS_STYLES.pending}`}>{r.status === "draft" ? "Draft" : r.status === "pending" ? `Awaiting ${r.approver_role === ROLES.FM ? "FM verification" : "CEO approval"}` : r.status === "approved" ? "Approved" : "Cancelled"}</span></TableCell>
                    <TableCell className="text-sm" data-testid={`receipt-approval-audit-${r.id}`}>
                      {r.status === "approved" && r.approved_by_name ? (
                        <div className="text-slate-600">
                          <div className="font-medium">{r.approved_by_role === ROLES.FM ? "Verified" : "Approved"} by {r.approved_by_name}</div>
                          <div className="text-xs text-slate-400">{fmtDate(r.approved_at)}</div>
                        </div>
                      ) : <span className="text-slate-300">—</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="stems-actions">
                        {r.status === "draft" && isCreator && <Button size="sm" className="bg-[#1E3A8A] hover:bg-[#1D4ED8]" onClick={() => act(r.id, "submit", `Sent to the ${sendTo} for approval.`)} data-testid={`receipt-submit-${r.id}`} title={`Send to the ${sendTo} for approval`}><CheckCircle2 className="h-4 w-4 mr-1" /> Send for approval</Button>}
                        {r.status === "approved" && <Button size="sm" variant="outline" onClick={() => preview(r)} data-testid={`receipt-print-${r.id}`} title="Preview & print"><Printer className="h-4 w-4" /></Button>}
                        {r.status === "pending" && isApprover && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => act(r.id, "approve", `${verb === "Verify" ? "Verified" : "Approved"}.`)} data-testid={`receipt-approve-${r.id}`}><CheckCircle2 className="h-4 w-4 mr-1" /> {verb}</Button>}
                        {(r.status === "draft" || r.status === "pending") && (isCreator || r.status === "pending") && <Button size="sm" variant="outline" onClick={() => startEdit(r)} data-testid={`receipt-edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>}
                        {r.status === "approved" && isApprover && <Button size="sm" variant="outline" onClick={() => act(r.id, "send-back", "Sent back for edits.")} data-testid={`receipt-sendback-${r.id}`} title="Send back for edits"><Undo2 className="h-4 w-4" /></Button>}
                        {r.status === "approved" && <Confirm onConfirm={() => act(r.id, "cancel", "Receipt cancelled.")} title="Cancel this receipt?" description={`${r.number} will be marked cancelled.`} actionLabel="Cancel receipt" trigger={<Button size="sm" variant="outline" className="text-amber-700" data-testid={`receipt-cancel-${r.id}`}><Ban className="h-4 w-4" /></Button>} />}
                        <Confirm onConfirm={() => remove(r.id)} title="Delete receipt?" description={`This permanently removes ${r.number}.`} trigger={<Button size="sm" variant="outline" className="text-red-600" data-testid={`receipt-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>} />
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
